import {
  dfaLoginApi,
  loginApi,
  logoutApi,
  userProLoginApi,
  partnerTokenExchangeApi,
  refreshSessionApi,
  selectCompanyApi
} from '@/services/AuthService'
import useLocalStorage from '@/composables/useLocalStorage'
import { AuthData, Credentials, PreAuthData } from './authTypes'
import { AuthState } from './authState'
import { AuthGetters } from './authGetters'
import store from '@/store/store'
import { ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { trackUser } from '@/plugins/tracker'
import { userflowLogin, userflowLogout } from '@/plugins/userflow'
import { Route } from '@/composables/useRouter'
import { AxiosError } from 'axios'

export function createActions(state: AuthState, getters: AuthGetters) {
  const { setItem, getItem, clear } = useLocalStorage()

  function reset() {
    state.preAuthData.value = {} as PreAuthData
    state.authData.value = {} as AuthData
    clear()
  }

  function clearPreAuthData() {
    state.preAuthData.value = {} as PreAuthData
  }

  function setAuthData(data: AuthData) {
    store.dispatch(`${ModuleNamespaces.AUTH}/setAuthData`, data) // TODO-VUEX_REMOVE
    state.authData.value = data
    setItem('authData', data)
  }

  const trackUserLogin = () => {
    trackUser()
    userflowLogin()
  }

  function setPreAuthData(data: PreAuthData) {
    state.preAuthData.value = data
  }

  async function login(credentials: Credentials) {
    clear()
    initialize()
    try {
      const { data } = await loginApi(credentials)
      if (data?.accessToken) {
        setAuthData(data)
        trackUserLogin()
        clearPreAuthData()
      } else {
        setPreAuthData(data)
      }
    } catch (error: any) {
      setError(error)
    } finally {
      state.loadingRef.value = false
    }
  }

  async function selectCompany(idCompany: string) {
    initialize()
    try {
      const { data } = await selectCompanyApi(idCompany, getters.preAuthToken.value)
      setAuthData(data)
      trackUserLogin()
    } catch (error) {
      setError(error)
    } finally {
      state.loadingRef.value = false
    }
  }

  async function logout() {
    state.loadingRef.value = true
    try {
      await logoutApi(getters.accessToken.value, getters.refreshToken.value)
    } catch (error) {
      // Ignore logout errors
      console.log('Logout error:', error)
    }

    userflowLogout()
    await store.dispatch(`${ModuleNamespaces.AUTH}/logout`)
    reset()
    state.loadingRef.value = false
  }

  async function getRefreshToken() {
    initialize()
    try {
      const refreshToken = getters.refreshToken.value
      const accessToken = getters.accessToken.value
      const params = { refreshToken, accessToken }

      const { data } = await refreshSessionApi(params)
      if (!data?.accessToken) {
        throw new Error('La sessión ha expirado')
      }
      setAuthData(data)
    } catch (error) {
      setError(error)
      cancelPendingRequests()
    } finally {
      state.loadingRef.value = false
    }
  }

  async function dfaLogin(uuid: string) {
    initialize()
    try {
      const { data } = await dfaLoginApi(uuid)
      if (data?.accessToken) {
        setAuthData(data)
        trackUserLogin()
      } else {
        setPreAuthData(data)
      }
    } catch (error) {
      setError(error)
    } finally {
      state.loadingRef.value = false
    }
  }

  async function partnerTokenExchange(partnerToken: string) {
    initialize()
    try {
      const { data } = await partnerTokenExchangeApi(partnerToken)
      setAuthData(data)
      trackUserLogin()
    } catch (error) {
      setError(error)
    } finally {
      state.loadingRef.value = false
    }
  }

  function setNextUrl(nextUrl: Route) {
    state.nextUrlData.value = nextUrl
  }

  async function userProLogout() {
    reset()
  }

  async function userProLogin(token: string) {
    initialize()
    try {
      const { data } = await userProLoginApi(token)
      if (data?.accessToken) {
        setAuthData(data)
        trackUserLogin()
      } else {
        setPreAuthData(data)
      }
    } catch (error) {
      state.errorData.value = getErrorMessage(error)
    } finally {
      state.loadingRef.value = false
    }
  }

  function cancelPendingRequests() {
    store.dispatch(`${ModuleNamespaces.AUTH}/cancelPendingRequests`) // TODO-VUEX_REMOVE
  }

  function initialize() {
    state.loadingRef.value = true
    state.errorData.value = null
  }

  const setError = (error: unknown) => {
    state.errorData.value = getErrorMessage(error)
  }

  // Initialize state from local storage
  const authDataLocal = getItem('authData')
  if (authDataLocal) {
    setAuthData(authDataLocal as AuthData)
  }

  return {
    login,
    selectCompany,
    logout,
    reset,
    setAuthData,
    partnerTokenExchange,
    getRefreshToken,
    dfaLogin,
    clearPreAuthData,
    setPreAuthData,
    setNextUrl,
    cancelPendingRequests,
    userProLogin,
    setError,
    userProLogout
  }
}

export type AuthActions = ReturnType<typeof createActions>

const ERROR_MAPPINGS: { [key: string]: string } = {
  ERR00000: 'Algo no salió como esperábamos. Vuelve a intentarlo.',
  ERR00004: 'Sesión expirada. Inicia sesión de nuevo',
  ERR00008: 'No se pudo conectar. Inténtalo de nuevo.',
  ERR00009: 'Usuario o contraseña no válidos.',
  ERR00010: 'Cuenta inactiva. Contacta con soporte.',
  ERR00011: 'Producto inactivo. Contacta con soporte.',
  ERR000014: 'Usuario inactivo. Contacta con el administrador.',
  ERR000015: 'Sesión expirada. Inicia sesión de nuevo',
  ERR000016: 'Cuenta deshabilitada. Contacta con soporte.',
  ERR000017: 'Cuenta bloqueada. Contacta con el administrador.',
  ERR000018: 'No se pudo conectar. Contacta con soporte.'
}

const getErrorMessage = (error: any): string => {
  if (error instanceof AxiosError) {
    return ERROR_MAPPINGS[error.response?.data?.code] || ERROR_MAPPINGS.ERR00000
  }

  if (error.code === 'ERR00004') {
    return ERROR_MAPPINGS[error.code] || ERROR_MAPPINGS.ERR00000
  }

  return ERROR_MAPPINGS.ERR00000
}
